import { AlertBanner, Skeleton } from '@curri/components'

import type { Resource } from '../../api/client.ts'
import type { WebhookSummary } from '../../api/webhooks.ts'

import { describeFailure, formatSuccessRate } from './format.ts'
import { StatTile, TILE_GRID } from './StatTile.tsx'

// The four numbers, and what each one is allowed to claim.
//
// Every "honest" decision the API made has a rendering here: a null success rate
// is "nothing sent", not 0%; dead endpoints are a current count rather than a
// windowed one, so they are labelled separately from the three that do respect
// the window.

const Tiles = ({ summary }: { summary: WebhookSummary }) => {
  const nothingSent = summary.delivered + summary.failed === 0
  // Lowercase like the design's mono annotations — this line is a stamp, not a
  // sentence.
  const windowHint = `last ${summary.windowDays} days`

  return (
    <div style={TILE_GRID}>
      <StatTile
        hint={windowHint}
        label="Delivered"
        value={summary.delivered.toLocaleString()}
      />
      <StatTile
        hint={windowHint}
        label="Failed"
        tone={summary.failed > 0 ? 'danger' : 'neutral'}
        value={summary.failed.toLocaleString()}
      />
      <StatTile
        hint={nothingSent ? 'nothing sent in this window' : windowHint}
        label="Success rate"
        value={formatSuccessRate(summary.successRate)}
      />
      <StatTile
        // Deliberately not the window: retry state carries no timestamp, so this
        // is a snapshot of endpoints that are dead right now.
        hint="current, not windowed"
        label="Dead endpoints"
        tone={summary.deadDeliveries > 0 ? 'danger' : 'neutral'}
        value={summary.deadDeliveries.toLocaleString()}
      />
    </div>
  )
}

export const SummaryStats = ({
  resource,
}: {
  resource: Resource<WebhookSummary>
}) => {
  if (resource.status === 'loading') {
    return (
      <div style={TILE_GRID}>
        {[0, 1, 2, 3].map(index => (
          <Skeleton key={index} style={{ height: 112 }} />
        ))}
      </div>
    )
  }

  if (resource.status === 'failed') {
    const copy = describeFailure(resource.failure)
    return (
      // A failed summary must not take the attempts table down with it, so this
      // renders in place of the tiles and the rest of the screen carries on.
      <AlertBanner title={copy.title} type={copy.tone}>
        {copy.body}
      </AlertBanner>
    )
  }

  return <Tiles summary={resource.data} />
}

export const DeadEndpointNotice = ({
  resource,
}: {
  resource: Resource<WebhookSummary>
}) => {
  if (resource.status !== 'ready' || resource.data.deadDeliveries === 0) {
    return null
  }

  return (
    <AlertBanner title="Curri has stopped retrying some deliveries" type="danger">
      {resource.data.deadDeliveries === 1
        ? '1 delivery has an endpoint that failed enough consecutive times that Curri gave up retrying it.'
        : `${resource.data.deadDeliveries} deliveries have an endpoint that failed enough consecutive times that Curri gave up retrying them.`}{' '}
      Fix the endpoint and email engineering@curri.com to have them re-sent.
    </AlertBanner>
  )
}
